import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { OverviewSection } from './OverviewSection';
import { SkillRegistrationSection } from './SkillRegistrationSection';
import { HistorySection } from './HistorySection';

/**
 * メンバー詳細ドロワー (設計書§3-2/§3-3)。Modal.tsx の position='right' を使う右スライドパネル。
 * 別セルの再クリックでは閉じずに employeeId/contextRoleProfileId を更新し「概要」タブへ戻す。
 *
 * v0.5 スキル登録UI刷新 (docs/design/v05-skill-registration-table-redesign-detail.md §3-8):
 * 旧「育成計画」独立タブは廃止し、`SkillRegistrationSection` (→ `SkillMatrixEditor`) の表内に
 * 目標レベル・目標達成時期の列として統合した。
 * §8-2 [第7版]: Drawerの横幅では表を読みやすく表示できないため、`SkillRegistrationSection`は
 * 表をこのDrawer内にインライン描画せず、`/employees/:id?tab=skills` (EmployeeDetailPageの
 * 「スキル・人材像」タブ、専用の広いページ) へのリンクに変更した。
 */

type TabKey = 'overview' | 'skills' | 'history';
const TAB_LABELS: Record<TabKey, string> = {
  overview: '概要', skills: 'スキル登録', history: '進捗履歴',
};

export function MemberDetailDrawer({
  open, employeeId, employeeName, contextRoleProfileId, onClose,
}: {
  open: boolean;
  employeeId: string;
  employeeName?: string;
  contextRoleProfileId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabKey>('overview');

  // ⚠️ 別セルの再クリックで employeeId/contextRoleProfileId が変わったときは、Drawer を
  // 閉じずに「概要」タブへ戻す (設計書§3-3 レビュー反映(第3版))。
  useEffect(() => {
    if (open) setTab('overview');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, contextRoleProfileId]);

  // ⚠️ 403 (スコープ外の employeeId) を受け取った場合、キャッシュを破棄して Drawer を閉じる
  // (security-reviewer Low指摘、設計書§3-3)。TanStack Query v5 は useQuery の onError を廃止した
  // ため、各セクション側で isError を監視し本コールバックを呼ぶ形にしている。
  const handleForbidden = () => {
    qc.removeQueries({ queryKey: ['employee-skill-gaps', employeeId] });
    qc.removeQueries({ queryKey: ['employee-skill-history', employeeId] });
    onClose();
  };

  return (
    <Modal open={open} position="right" title={employeeName ? `${employeeName} さんの詳細` : 'メンバー詳細'} onClose={onClose}>
      {employeeId && (
        <div className="space-y-4">
          <div role="tablist" aria-label="メンバー詳細のタブ" className="flex flex-wrap gap-1 border-b border-surface-line -mt-1">
            {(Object.keys(TAB_LABELS) as TabKey[]).map((t) => (
              <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
                className={`px-2.5 py-2 text-xs font-semibold min-h-[44px] border-b-2 -mb-px ${
                  tab === t ? 'border-misespark-navy text-misespark-navy' : 'border-transparent text-text-sub hover:text-text-body'
                }`}>
                {TAB_LABELS[t]}
              </button>
            ))}
          </div>
          <div role="tabpanel">
            {tab === 'overview' && (
              <OverviewSection employeeId={employeeId} roleProfileId={contextRoleProfileId} onForbidden={handleForbidden} />
            )}
            {tab === 'skills' && <SkillRegistrationSection employeeId={employeeId} />}
            {tab === 'history' && <HistorySection employeeId={employeeId} onForbidden={handleForbidden} />}
          </div>
        </div>
      )}
    </Modal>
  );
}
